/**
 * Zustand slice tracking SHX font names referenced by the currently
 * loaded drawing but NOT yet present in `FontRegistry`.
 *
 * Populated by the renderer: whenever `renderText` / `renderMText` hit
 * a style whose `fontName` resolves to `FontRegistry.has(name) === false`
 * they call `useMissingFontsStore.getState().add(name)` and fall back
 * to the built-in SVG `<text>` element.
 *
 * Drained by the UI: `FontManagerDialog` subscribes via the hook and
 * renders the set as a list next to each loaded font so the user sees
 * "drawing references X but it is not loaded".
 *
 * ### Normalisation
 *
 * Names are stored normalised (lower-case, trimmed, `.shx` stripped)
 * so case-differing references (`"ROMANS.SHX"` vs `"romans"`) collapse
 * to a single entry. This matches `FontRegistry.normalize`.
 *
 * ### Why Zustand?
 *
 * Consistency with `apps/web/src/app/i18n/index.ts` — the rest of the
 * project already uses `import { create } from "zustand"` for small
 * global stores, so we follow the same pattern rather than introduce
 * yet another manual `useSyncExternalStore` store. The renderer
 * accesses the store via `.getState().add()` (outside of React), which
 * is the documented escape-hatch for zustand.
 */

import { create } from "zustand";

import { normalizeFontKey } from "./fontStore";

/**
 * Shape of the missing-fonts store. `missing` is a `Set<string>` of
 * normalised keys; consumers iterate it directly to render a list.
 */
type MissingFontsState = {
  missing: Set<string>;
  /** Register a font name that could not be resolved to a loaded font. */
  add: (name: string) => void;
  /** Drop a single font from the set (e.g. after it's been uploaded). */
  remove: (name: string) => void;
  /** Clear the whole set (e.g. when opening a new drawing). */
  clear: () => void;
  /** Snapshot for tests / diagnostic logs. */
  has: (name: string) => boolean;
};

export const useMissingFontsStore = create<MissingFontsState>((set, get) => ({
  missing: new Set<string>(),

  add: (name) =>
    set((state) => {
      const normalised = normalizeFontKey(name);
      if (normalised === "") return state;
      if (state.missing.has(normalised)) return state;
      const next = new Set(state.missing);
      next.add(normalised);
      return { missing: next };
    }),

  remove: (name) =>
    set((state) => {
      const normalised = normalizeFontKey(name);
      if (!state.missing.has(normalised)) return state;
      const next = new Set(state.missing);
      next.delete(normalised);
      return { missing: next };
    }),

  clear: () =>
    set((state) => {
      if (state.missing.size === 0) return state;
      return { missing: new Set<string>() };
    }),

  has: (name) => get().missing.has(normalizeFontKey(name)),
}));

/**
 * Selector hook returning the list of missing font names sorted A→Z.
 * Components that just need to render the list use this so they don't
 * have to destructure `Set` manually on every render.
 */
export function useMissingFontsList(): string[] {
  const missing = useMissingFontsStore((s) => s.missing);
  return Array.from(missing).sort((a, b) => a.localeCompare(b));
}
